import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

//Common
import { LoadingIndicator } from "../../common/VFX-UI";

//App
import Campaigns from "./Campaigns";
import {
    selectIsActive,
    selectSearchValue,
    setIsActive,
    setSearchValue,
} from "../../../store/campaign";
import { CampaignContextData, CampaignUseState } from "../../../types/campaign";
import { SortBy } from "../../../types";
import { SearchValue } from "../../../store/types";
import { EMPTY_GUID, GRID_PAGE_SIZE } from "../../../utility/constants";
import { queryKeys } from "../../Campaign/constants";
import organizationApi from "../../../app/model/api/organizationApi";
import {
    getOrganizationId,
    IsOrganizationUser,
} from "../../../utility/helpers/claimHelper";

export const CampaignReportContext =
    React.createContext<CampaignContextData | null>(null);

const CampaignsReportsContainer = () => {
    const dispatch = useDispatch();

    const [state, setState] = useState<CampaignUseState>({
        campaignId: null,
        activeToggle: useSelector(selectIsActive) ?? true,
        searchPayload: useSelector(selectSearchValue) ?? null,
        pageSize: GRID_PAGE_SIZE,
        currentPage: 1,
        sortBy: [{ field: "name", direction: 1 }],
    });

    const [isCompleted, setIsCompleted] = useState<boolean | null>(false);
    const organizationId = IsOrganizationUser() ? getOrganizationId() : window.location.pathname.split("/")[2] ?? EMPTY_GUID;

    const campaignGridQuery = useQuery({
        queryKey: [queryKeys.searchOrganizationCampaign],
        queryFn: () =>
            organizationApi.searchOrganizationCampaign({
                ...state.searchPayload,
                organizationId: organizationId,
                isActive: isCompleted !== true ? state.activeToggle : undefined,
                isCompleted: isCompleted === true ? true : null,
                pageSize: state.pageSize,
                pageNumber: state.currentPage,
                sortBy: state.sortBy,
            }),
    });

    const { data: campaignLookupData } = useQuery({
        queryKey: [queryKeys.getOrganizationReportCampaignLookups],
        queryFn: () => organizationApi.getOrganizationCampaignLookups(),
    });

    const campaignQuery = useQuery({
        queryKey: [queryKeys.getOrganizationCampaign, state.campaignId],
        queryFn: () =>
            organizationApi.getOrganizationCampaign(state.campaignId ?? EMPTY_GUID),
        enabled: state.campaignId !== null,
    });

    const { mutate: deleteMutation } = useMutation<void, Error, string>({
        mutationFn: async (value: string) => {
            await organizationApi.deactivateOrganizationCampaign(value);
        },
        onError: () => console.log("error"),
        onSuccess: () => console.log("success"),
    });

    const { mutate: activateMutation } = useMutation<void, Error, string>({
        mutationFn: async (value: string) => {
            await organizationApi.activateOrganizationCampaign(value);
        },
        onError: () => console.log("error"),
        onSuccess: () => console.log("success"),
    });

    const updateCampaignId = async (campaignId: string | null) => {
        setState((prevState) => ({
            ...prevState,
            campaignId: campaignId,
        }));
    };

    const updateActiveToggle = async () => {
        dispatch(setIsActive());
        setState((prevState) => ({
            ...prevState,
            activeToggle: !prevState.activeToggle,
        }));
    };

    const updateSearchPayload = async (values: SearchValue | null) => {
        if (values?.name) {
            dispatch(setSearchValue({ name: values.name }));
        }
        if (values && Object.keys(values).length) {
            setState((prevState) => ({
                ...prevState,
                searchPayload: values,
                currentPage: 1,
            }));
        } else {
            setState((prevState) => ({
                ...prevState,
                searchPayload: null,
                currentPage: 1,
                sortBy: [{ field: "name", direction: 1 }],
            }));
        }
    };

    const updateCurrentPage = async (currentPage: number) => {
        setState((prevState) => ({
            ...prevState,
            currentPage: currentPage,
        }));
    };

    const updateCurrentPageSize = async (pageSize: number) => {
        setState((prevState) => ({
            ...prevState,
            pageSize: pageSize,
        }));
    };

    const updateSortBy = async (sortBy: SortBy) => {
        const { field, direction } = sortBy;
        setState((prevState) => ({
            ...prevState,
            sortBy: [{ field, direction }],
        }));
    };

    const contextValue: CampaignContextData = {
        data: campaignQuery?.data?.result,
        organizations: campaignLookupData?.result?.organizations,
        id: state.campaignId,
        pageSize: state.pageSize,
        activeToggle: state.activeToggle,
        setId: updateCampaignId,
        refetchGrid: () => campaignGridQuery.refetch(),
        setActiveToggleValue: updateActiveToggle,
        setSearchValue: updateSearchPayload,
        setCurrentPage: updateCurrentPage,
        setSortBy: updateSortBy,
        setCurrentPageSize: updateCurrentPageSize,
        paging: {
            currentPage: state.currentPage,
            totalSize: campaignGridQuery.data?.totalSize ?? 0,
        },
    };

    return campaignGridQuery.isLoading ? (
        <LoadingIndicator />
    ) : (
        <CampaignReportContext.Provider value={contextValue}>
            <Campaigns
                searchValue={state.searchPayload?.name ?? ""}
                data={campaignGridQuery?.data?.result}
                deleteMutation={deleteMutation}
                activateMutation={activateMutation}
            />
        </CampaignReportContext.Provider>
    );
};

export default CampaignsReportsContainer;
